/**
 * Renderer comun pentru meniurile Tiptap Suggestion (`/`, `@`, `&`, `[[`):
 * popover tippy lângă cursor, navigare cu săgeți, Enter selectează,
 * Escape închide.
 */
import type { ComponentType } from 'react';
import { ReactRenderer } from '@tiptap/react';
import type {
  SuggestionKeyDownProps,
  SuggestionOptions,
  SuggestionProps,
} from '@tiptap/suggestion';
import tippy, { type Instance as TippyInstance } from 'tippy.js';

export interface SuggestionListProps<T> {
  items: T[];
  command: (item: T) => void;
  selectedIndex: number;
}

export function makeSuggestionRenderer<T>(
  List: ComponentType<SuggestionListProps<T>>,
  canSelect: (item: T) => boolean = () => true,
): NonNullable<SuggestionOptions<T, T>['render']> {
  return () => {
    let component: ReactRenderer | null = null;
    let popup: TippyInstance[] | null = null;
    let current: SuggestionProps<T, T> | null = null;
    let selectedIndex = 0;

    const sync = () => {
      if (component && current) component.updateProps({ ...current, selectedIndex });
    };

    return {
      onStart: (props) => {
        current = props;
        selectedIndex = 0;
        component = new ReactRenderer(List, {
          props: { ...props, selectedIndex },
          editor: props.editor,
        });
        if (!props.clientRect) return;
        popup = tippy('body', {
          getReferenceClientRect: props.clientRect as () => DOMRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
        });
      },
      onUpdate: (props) => {
        current = props;
        sync();
        if (popup?.[0] && props.clientRect) {
          popup[0].setProps({
            getReferenceClientRect: props.clientRect as () => DOMRect,
          });
        }
      },
      onKeyDown: ({ event }: SuggestionKeyDownProps) => {
        const items = current?.items;
        if (!current || !items || items.length === 0) return false;
        if (event.key === 'ArrowUp') {
          selectedIndex = (selectedIndex + items.length - 1) % items.length;
          sync();
          return true;
        }
        if (event.key === 'ArrowDown') {
          selectedIndex = (selectedIndex + 1) % items.length;
          sync();
          return true;
        }
        if (event.key === 'Enter') {
          const item = items[selectedIndex];
          if (item !== undefined && canSelect(item)) {
            current.command(item);
            return true;
          }
        }
        if (event.key === 'Escape') {
          popup?.[0]?.hide();
          return true;
        }
        return false;
      },
      onExit: () => {
        popup?.[0]?.destroy();
        component?.destroy();
        popup = null;
        component = null;
        current = null;
      },
    };
  };
}
